import { hash } from 'ohash';

/**
 * Groups API activity booking parts into a product info for activities
 *
 * 1. Filter out only activity booking parts
 * 2. Group them by activity, passengers and dates
 */
export function transformProductInfosActivity(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse']): ProductInfoActivity[] {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    return bookingParts
        .filter(bp => bp.objectType === 'Activity')
        .filter(bp => bp.status !== 'REFUNDED')
        .reduce<ProductInfoActivity[]>((list, bookingPart) => {
            assignActivity(list, bookingPart, apiBooking);
            return list;
        }, []);
}

/**
 * Assigns an activity booking part to the appropriate product info.
 *
 * Booking parts that belong together are grouped together. Based on:
 * - Activity details
 * - Passengers
 * - Dates
 *
 * As a result you get one activity product info per activity/passenger/date combination, which is how we want to display it in the app.
 *
 * Example: 4 People go skiing in february, 3 people go rafting im september
 * - 4 booking parts --> grouped into 1 product info with 4 passengers (skiing february)
 * - 3 booking parts --> grouped into another product info with 3 passengers (rafting september)
 */
function assignActivity(
    list: ProductInfoActivity[],
    activity: ApiSchema['Activity'],
    apiBooking: NonNullable<ApiSchema['BookingResponse']['booking']>,
) {
    const activityId = activity.activityId ?? '';
    const type = 'Activity';
    const dateFrom = zurichDate(activity.validFrom);
    const dateUntil = zurichDate(activity.validUntil);
    const passengers = apiBooking.passengers
        .filter(p => activity.passengerIds.includes(p.id))
        .map(p => transformPassenger(p));

    // Find local product config for this activity. This is then used to:
    // - Check if there is a dynamic listingId added to the activity (dynamic product)
    // - Else, take the static listingId from the config (static product)
    // Rental add-ons (helmet, boots, ...) are activities of their own, but are booked under the rental's listing
    const config = productConfigs.find(c => c.type === 'Activity'
        && (c.activityId === activityId || (!!activity.listingId && c.listingId === activity.listingId)));
    const listingId = activity.listingId ?? config?.listingId;

    // Group items by this identifier. The listing groups rental add-ons with their equipment.
    const id = hash([
        listingId ?? activityId,
        type,
        dateFrom.getTime(),
        dateUntil?.getTime(),
    ]);

    // Find or create (first) product info item
    let productInfo = list.find(p => p.id === id);
    if (!productInfo) {
        productInfo = {
            id,
            activityId,
            type,
            listingId,
            dateFrom,
            dateUntil,
            passengers: [],
            ...(activity.startingPoint && { startingPoint: activity.startingPoint }),
            ...(config?.isRental && { rentalDetails: { items: [] } }),
        } satisfies ProductInfoActivity;
        list.push(productInfo);
    }

    // Add passengers of this booking part, that are not yet part of the product info
    productInfo.passengers.push(...passengers.filter(p => !productInfo.passengers.some(existing => existing.id === p.id)));

    // Rentals list the booked equipment and add-ons per passenger
    activity.passengerIds.forEach(passengerId => productInfo.rentalDetails?.items.push({ passengerId, summary: activity.summary ?? '' }));
}
